import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import type {
  IdentityOption,
  LabeledOption,
  ResolvedOptions,
} from "@/shared/components/ResourceView";
import type { Device } from "@/shared/domain/devices";
import { QueryContentMode, QueryTarget } from "@/shared/domain/query";
import { RobotoPrincipal, RobotoPrincipalType } from "@/shared/principal";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import type { Condition } from "@/types";

/** The domain-service bag, read lazily so unused services are never resolved. */
type DomainServices = ReturnType<typeof useDomainServices>;

/**
 * How many entries a directory fetches on open. The picker asks the server for
 * one page this size with no search term; if everything fit, it latches that
 * set and filters it in memory from then on, and if it didn't, it stays
 * server-searched and shows a "refine your search" hint. So this is the
 * threshold at which a directory stops being loaded whole — not a display cap.
 *
 * Small on purpose: most orgs' member and device lists fall under it and get
 * the zero-latency client-side path, while a fleet-scale org pays for one
 * bounded page instead of a full pagination walk.
 */
const DIRECTORY_PAGE_SIZE = 20;

/** No org means nothing to scope a directory search to. */
const EMPTY_DIRECTORY: ResolvedOptions = { options: [], truncated: false };

/**
 * The ``options`` for an ``identity`` filter over an audit principal column
 * (``created_by`` / ``modified_by``) — one entry per principal type such a
 * column can hold, in operator-pill order, each carrying the resolver that
 * backs its picker. Hand it straight to a {@link IdentityOption} list; the same
 * array serves both fields on a page.
 *
 * The type list is the complete set, not a per-surface guess: every write path
 * stamps ``identity.audit_field_principal`` (``roboto_service``'s
 * ``auth/identity.py``), which returns a device principal for
 * device-authenticated traffic, an invocation principal for action traffic, and
 * a user principal otherwise. No create route for datasets, files, events,
 * sessions, or devices denies device or invocation callers, so all three types
 * are reachable on every one of them.
 *
 * Every resolver is LAZY: the picker invokes it only when that type's operator
 * is selected. Nothing is fetched merely by calling this hook, which matters
 * because the pages defining these filters call it at the top level on every
 * visit and most of those visits never open the filter menu. It also means a
 * backing service is read only on demand — the domain context resolves services
 * through throwing getters, so a page whose user never opens the Device pill
 * never touches ``DeviceService``.
 *
 * Fetches go through the app query client under a fully-qualified key rather
 * than the picker's own per-mount cache. Two consequences, both load-bearing:
 * reopening the filter menu inside the client's ``staleTime`` costs no request,
 * and ``created_by`` and ``modified_by`` — and every page calling this hook —
 * share one cached directory instead of each holding a private copy. What lands
 * in the cache is the MAPPED option list, so a hit costs neither a request nor
 * a re-map.
 *
 * Each resolver drops the ``AbortSignal`` the picker offers it and uses the
 * query client's instead. That is deliberate: a cache entry is shared across
 * pickers, so one picker's superseded search must not abort a fetch another is
 * still waiting on. The query client aborts once nothing is left waiting.
 */
export function useAuditIdentityOptions(
  orgId: string | undefined,
): readonly [IdentityOption, ...IdentityOption[]] {
  const domainServices = useDomainServices();
  const queryClient = useQueryClient();

  return React.useMemo(
    () => [
      {
        type: RobotoPrincipalType.User,
        optionResolver: (search: string) =>
          queryClient.ensureQueryData({
            queryKey: ["identity-options", "user", orgId, search],
            queryFn: ({ signal }) =>
              searchUsers(domainServices, orgId, search, signal),
          }),
      },
      {
        type: RobotoPrincipalType.Device,
        optionResolver: (search: string) =>
          queryClient.ensureQueryData({
            queryKey: ["identity-options", "device", orgId, search],
            queryFn: ({ signal }) =>
              searchDevices(domainServices, orgId, search, signal),
          }),
      },
      {
        type: RobotoPrincipalType.Invocation,
        optionResolver: resolveInvocationOption,
      },
    ],
    [domainServices, queryClient, orgId],
  );
}

/**
 * The OR-of-CONTAINS condition a picker's typed term becomes, matching the
 * given columns case-insensitively. `undefined` (no condition) for an empty
 * term — the on-open page lists the directory unfiltered.
 */
function containsAnyCondition(
  term: string,
  fields: readonly string[],
): Condition | undefined {
  if (!term) {
    return undefined;
  }
  const conditions = fields.map((field) => ({
    field,
    comparator: "CONTAINS" as const,
    value: term,
  }));
  return conditions.length === 1
    ? conditions[0]
    : { operator: "OR", conditions };
}

/** ``user`` → ``user:<user_id>`` labeled by display name (falling back to the id). */
async function searchUsers(
  domainServices: DomainServices,
  orgId: string | undefined,
  search: string,
  signal: AbortSignal,
): Promise<ResolvedOptions> {
  if (orgId === undefined) {
    return EMPTY_DIRECTORY;
  }
  const condition = containsAnyCondition(search, ["user_id", "name"]);
  const page = await domainServices.orgService.searchOrgUsers(
    orgId,
    { ...(condition && { condition }), limit: DIRECTORY_PAGE_SIZE },
    { abortSignal: signal },
  );
  return {
    options: collectIdentityOptions(page.items, (user) => [
      () => RobotoPrincipal.forUser(user.user_id),
      user.name ?? user.user_id,
    ]),
    // A continuation token means the server had more matches than it returned,
    // which keeps the picker in server-searched mode.
    truncated: Boolean(page.next_token),
  };
}

/**
 * ``device`` → ``device:<device_id>@<org_id>`` labeled by device id.
 *
 * Devices go through the unified query endpoints (target ``devices``) rather
 * than a bespoke search route: submit a structured query whose condition is
 * ``device_id CONTAINS <term>``, then read the first page of results. The
 * ``CONTAINS`` comparator matches case-insensitively server-side (SQL
 * ``ILIKE``), and ``device_id`` ordering keeps offset pages disjoint.
 */
async function searchDevices(
  domainServices: DomainServices,
  orgId: string | undefined,
  search: string,
  signal: AbortSignal,
): Promise<ResolvedOptions> {
  if (orgId === undefined) {
    return EMPTY_DIRECTORY;
  }
  const condition = containsAnyCondition(search, ["device_id"]);
  const record = await domainServices.queryService.submitStructured(
    {
      content_mode: QueryContentMode.RecordWithMeta,
      target: QueryTarget.Devices,
      query: {
        ...(condition && { condition }),
        sort_by: "device_id",
        sort_direction: "ASC",
        limit: DIRECTORY_PAGE_SIZE,
      },
    },
    { resourceOwnerId: orgId, abortSignal: signal },
  );
  const page = await domainServices.queryService.getQueryResults<Device>(
    record.query_id,
    { pageSize: DIRECTORY_PAGE_SIZE, abortSignal: signal },
  );
  return {
    options: collectIdentityOptions(page.items, (device) => [
      () => RobotoPrincipal.forDevice(device.device_id, device.org_id),
      device.device_id,
    ]),
    truncated: Boolean(page.next_token),
  };
}

/**
 * ``invocation`` → ``invocation:<typed id>``, resolved locally.
 *
 * Invocation ids are opaque and high-cardinality, so there is no directory to
 * search and no endpoint to ask: the option set is whatever the user typed.
 * Echoing it back as a single option keeps this kind on the same picker as the
 * others instead of needing a bespoke free-text input.
 *
 * Truncation is reported on the empty on-open search so the picker never
 * latches that page as "the complete option set" — a latch would freeze the
 * list empty and stop it ever asking again. A search that produced an option is
 * complete by construction, so it reports none and no "refine your search" hint
 * appears.
 */
function resolveInvocationOption(search: string): Promise<ResolvedOptions> {
  const invocationId = search.trim();
  if (invocationId === "") {
    return Promise.resolve({ options: [], truncated: true });
  }
  return Promise.resolve({
    options: collectIdentityOptions([invocationId], (id) => [
      () => RobotoPrincipal.forInvocation(id),
      id,
    ]),
    truncated: false,
  });
}

/**
 * Map directory records to labeled identity options, skipping any record whose
 * id would throw in ``RobotoPrincipal`` — an id containing a ``:``, see
 * ``RobotoPrincipal.validateId``.
 *
 * Dropping the record keeps one malformed entry from taking the whole filter
 * menu down, but the drop is REPORTED rather than swallowed: an id that can't
 * form a principal is a data problem, and since the same id is what gets
 * audited into ``created_by`` / ``modified_by``, that record's rows are
 * unfilterable by identity. Omitted silently, it just looks to the user like a
 * member who isn't in the org.
 */
function collectIdentityOptions<T>(
  records: readonly T[] | undefined,
  toEntry: (record: T) => [build: () => RobotoPrincipal, label: string],
): LabeledOption[] {
  const options: LabeledOption[] = [];
  for (const record of records ?? []) {
    const [build, label] = toEntry(record);
    try {
      options.push({ value: build().toString(), label });
    } catch (error) {
      ErrorMonitoringService.captureError(error, {
        context: "identity filter options",
        label,
      });
    }
  }
  return options;
}
